/**
 * Feature 022 — session cache for the notes search index.
 *
 * Building the index means fetching every chapter's HTML (~770 KB) and parsing each into example
 * blocks with their reader page/section. That is affordable once, but `NotesSearchPage` remounts on
 * every visit — and the natural loop is search → open a result → come back → refine — so without a
 * cache the learner re-downloads and re-parses the whole book on each lap.
 *
 * The cache is module state, so it lives exactly as long as the SPA session: any full page reload
 * drops it. That bounds how stale it can get, which matters because the chapter list carries no
 * content version to key on — a chapter whose HTML is redeployed mid-session (without a manifest
 * change) would be served from cache until the next reload. The manifest itself is re-fetched on
 * every call, so added/removed/renamed/reordered chapters DO invalidate the cache immediately.
 *
 * Chapters are cached INDIVIDUALLY (FR-044, research D9). A build where some chapters load and others
 * do not is neither a clean success nor a clean failure, so caching at whole-index granularity would
 * force a bad trade: cache it and a momentary blip strands a chapter as missing for the rest of the
 * session; cache nothing and a permanently absent chapter re-parses the whole book on every visit,
 * destroying the win FR-042 exists to protect. Per-chapter caching keeps the successes and retries
 * only what failed.
 */

import { learnApi, type ChapterSummary } from '../api/learnApi';
import { buildChapterIndex, type ChapterSearchIndex } from './notesSearch';

/** What a load produced: the chapters that could be read, and those that could not (FR-044). */
export interface SearchIndexLoad {
  /** Readable chapters, in manifest order. */
  indexes: ChapterSearchIndex[];
  /**
   * Chapters whose content could not be read. Returned alongside `indexes` rather than through a
   * side channel so a caller cannot render results while ignoring that the corpus is incomplete.
   */
  unavailable: ChapterSummary[];
}

let cached: { signature: string; bySlug: Map<string, ChapterSearchIndex> } | null = null;
let inFlight: { signature: string; promise: Promise<SearchIndexLoad> } | null = null;

/** Identity of the chapter set; a change here means the cached parse is for a different book. */
function signatureOf(chapters: ChapterSummary[]): string {
  return chapters.map((c) => `${c.slug}:${c.order}:${c.title}`).join('|');
}

/**
 * Fetch and parse every chapter not already cached, tolerating individual failures. Uses
 * `allSettled`, not `all`: one rejected chapter must not reject the whole build (FR-044).
 */
async function fetchMissing(
  chapters: ChapterSummary[],
  bySlug: Map<string, ChapterSearchIndex>,
): Promise<ChapterSummary[]> {
  const missing = chapters.filter((ch) => !bySlug.has(ch.slug));

  const settled = await Promise.allSettled(
    missing.map(async (ch) => buildChapterIndex(await learnApi.getChapterContent(ch.slug), ch)),
  );

  const unavailable: ChapterSummary[] = [];
  settled.forEach((result, i) => {
    const chapter = missing[i]!;
    if (result.status === 'fulfilled') bySlug.set(chapter.slug, result.value);
    else unavailable.push(chapter);
  });
  return unavailable;
}

/**
 * The parsed index for every chapter that can be read, built once per SPA session. Concurrent
 * callers share one build rather than racing duplicate fetches.
 *
 * Rejects ONLY when no chapter could be read at all — a partial result is a usable search the caller
 * must disclose (FR-044), not an error. An empty manifest resolves empty: nothing failed, there is
 * simply nothing to search.
 */
export async function loadSearchIndexes(): Promise<SearchIndexLoad> {
  // The manifest is small; re-fetching it each time is what lets us detect a changed chapter set
  // instead of pinning the first one we ever saw.
  const chapters = await learnApi.getChapters();
  const signature = signatureOf(chapters);

  // A cache hit only counts when every chapter is present; otherwise fall through and retry the
  // stragglers, so a chapter that failed earlier gets another chance on this visit.
  if (cached?.signature === signature && chapters.every((ch) => cached!.bySlug.has(ch.slug))) {
    return { indexes: orderedIndexes(chapters, cached.bySlug), unavailable: [] };
  }
  if (inFlight?.signature === signature) return inFlight.promise;

  const bySlug = cached?.signature === signature ? cached.bySlug : new Map<string, ChapterSearchIndex>();

  const promise = fetchMissing(chapters, bySlug)
    .then((unavailable) => {
      if (chapters.length > 0 && bySlug.size === 0) {
        // Nothing at all could be read — the caller shows an outright failure rather than an
        // empty-looking search. Not cached, so the next visit retries.
        throw new Error('No Precision Notes chapters could be loaded.');
      }
      cached = { signature, bySlug };
      return { indexes: orderedIndexes(chapters, bySlug), unavailable };
    })
    .finally(() => {
      if (inFlight?.promise === promise) inFlight = null;
    });

  inFlight = { signature, promise };
  return promise;
}

/** Cached chapters in manifest order — a chapter fetched late (a retry) must not sort last. */
function orderedIndexes(
  chapters: ChapterSummary[],
  bySlug: Map<string, ChapterSearchIndex>,
): ChapterSearchIndex[] {
  return chapters.map((ch) => bySlug.get(ch.slug)).filter((i): i is ChapterSearchIndex => !!i);
}

/** Test seam — drops the cached index so each test starts from a cold cache. */
export function resetSearchIndexCache(): void {
  cached = null;
  inFlight = null;
}
